import * as React from "react";
import { FormControl, FormLabel, Select, Option, Typography } from "@mui/joy";
import { ChevronDown } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

interface OptionType {
    value: string;
    label: string;
}

interface FormSelectProps {
    name: string;
    label: string;
    helperText?: string;
    options: OptionType[] | string[];
    startDecorator?: React.ReactNode;
    onValueChange?: (value: string | null) => void;
    [key: string]: any;
}

export default function FormSelect({
    name,
    label,
    helperText,
    options,
    startDecorator,
    onValueChange,
    ...props
}: FormSelectProps) {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <FormControl size="md" error={!!fieldState.error}>
                    <FormLabel sx={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "xl", letterSpacing: "0.5px" }}>
                        {label}
                    </FormLabel>
                    {helperText && (
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            {helperText}
                        </Typography>
                    )}
                    <Select
                        {...field}
                        startDecorator={startDecorator}
                        indicator={<ChevronDown size={16} />}
                        sx={{ bgcolor: "background.surface", borderRadius: "4px", boxShadow: "none" }}
                        {...props}
                        onChange={(_, newValue) => {
                            field.onChange(newValue);
                            onValueChange?.(newValue);
                        }}
                    >
                        {options.map((opt: any) => {
                            if (typeof opt === "string") {
                                return <Option key={opt} value={opt}>{opt}</Option>;
                            }
                            return <Option key={opt.value} value={opt.value}>{opt.label}</Option>;
                        })}
                    </Select>
                    {fieldState.error && (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                            {fieldState.error.message}
                        </Typography>
                    )}
                </FormControl>
            )}
        />
    );
}
